// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the To line of the Monday Madness email: people as chips, one field to add more.
// Type an address or "Name <address>" and press Enter, comma or leave the field.

import {parseRecipient} from '@app/features/konvo_reports/utils/MondayDraftEdit';
import type {MondayReportDraft} from '@fluxer/schema/src/domains/konvo/MondayReportDraft';
import {useLingui} from '@lingui/react/macro';
import {IconX} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {useState} from 'react';
import styles from './MondayReportCard.module.css';

type Recipients = MondayReportDraft['recipients'];

export function MondayRecipients({
	recipients,
	onChange,
}: {
	recipients: Recipients;
	onChange: (recipients: Recipients) => void;
}) {
	const {t} = useLingui();
	const [text, setText] = useState('');
	const [invalid, setInvalid] = useState(false);
	const commit = () => {
		if (!text.trim()) return true;
		const people = text
			.split(/[,;\n]/)
			.map((part) => part.trim())
			.filter(Boolean);
		const parsed = people.map(parseRecipient);
		if (parsed.some((person) => !person)) {
			setInvalid(true);
			return false;
		}
		const known = new Set(recipients.map((person) => person.address.toLowerCase()));
		const added = parsed.filter((person): person is NonNullable<typeof person> => {
			if (!person || known.has(person.address.toLowerCase())) return false;
			known.add(person.address.toLowerCase());
			return true;
		});
		onChange([...recipients, ...added].slice(0, 20));
		setText('');
		setInvalid(false);
		return true;
	};
	return (
		<div className={styles.recipients}>
			{recipients.map((person, index) => (
				<span key={person.address} className={styles.person} title={person.address}>
					<span className={styles.personName}>{person.name || person.address}</span>
					<button
						type="button"
						aria-label={t`Remove ${person.name || person.address}`}
						title={t`Remove`}
						onClick={() => onChange(recipients.filter((_, i) => i !== index))}
					>
						<IconX size={12} stroke={2} aria-hidden="true" />
					</button>
				</span>
			))}
			{recipients.length < 20 && (
				<input
					className={clsx(styles.recipientInput, invalid && styles.invalid)}
					aria-label={t`Add a recipient`}
					aria-invalid={invalid || undefined}
					placeholder={recipients.length ? t`Add someone` : t`Name <name@example.com>`}
					value={text}
					maxLength={400}
					onChange={(event) => {
						setText(event.target.value);
						setInvalid(false);
					}}
					onKeyDown={(event) => {
						if ((event.key === 'Enter' || event.key === ',' || event.key === ';') && !event.nativeEvent.isComposing) {
							event.preventDefault();
							commit();
						} else if (event.key === 'Backspace' && !text && recipients.length) {
							onChange(recipients.slice(0, -1));
						}
					}}
					onBlur={commit}
				/>
			)}
			{invalid && <span className={styles.fieldError}>{t`Use an email address, like name@example.com`}</span>}
		</div>
	);
}
